import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert, Divider
} from '@mui/material';
import {
  ShoppingCart, AttachMoney, People, DirectionsCar,
  TrendingUp, Warning, ArrowForward, Refresh
} from '@mui/icons-material';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import { reportsAPI } from '../services/api';
import StatCard from '../components/common/StatCard';
import { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);

const PIE_COLORS = ['#1a237e', '#3949ab', '#7986cb', '#c5cae9', '#e8eaf6'];

const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: res } = await reportsAPI.getDashboard();
      setData(res.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
      <CircularProgress size={48} />
    </Box>
  );

  if (error) return (
    <Alert severity="error" action={<Button onClick={fetchDashboard}>Retry</Button>}>{error}</Alert>
  );

  const s = data?.sales || {};
  const inv = data?.inventory || {};
  const cust = data?.customers || {};

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} variant="outlined" onClick={fetchDashboard}>Refresh</Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Revenue Today" value={fmt(s.revenue_today)} icon={<AttachMoney />}
            color="primary" subtitle={`${fmtNum(s.orders_today)} orders`} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Monthly Revenue" value={fmt(s.revenue_month)} icon={<TrendingUp />}
            color="success" subtitle={`${fmtNum(s.orders_month)} orders this month`} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Total Products" value={fmtNum(inv.total_products)} icon={<DirectionsCar />}
            color="info" subtitle={`${fmtNum(inv.available)} available`} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title="Total Customers" value={fmtNum(cust.total_customers)} icon={<People />}
            color="warning" subtitle={`+${fmtNum(cust.new_this_month)} new this month`} />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Revenue Chart */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Revenue Trend (Last 12 Months)</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data?.revenue_chart || []}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1a237e" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#1a237e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [fmt(v), 'Revenue']} />
                  <Area type="monotone" dataKey="revenue" stroke="#1a237e" strokeWidth={2}
                    fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Inventory Status Pie */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Inventory Status</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={[
                    { name: 'Available', value: parseInt(inv.available) || 0 },
                    { name: 'Sold', value: parseInt(inv.sold) || 0 },
                    { name: 'Reserved', value: parseInt(inv.low_stock_count) || 0 },
                  ]} cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                    paddingAngle={4} dataKey="value">
                    {PIE_COLORS.map((color, i) => <Cell key={i} fill={color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bottom Row */}
      <Grid container spacing={3}>
        {/* Recent Orders */}
        <Grid item xs={12} lg={7}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Recent Orders</Typography>
                <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/sales')}>View All</Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order #</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.recent_orders || []).slice(0, 8).map((order) => (
                      <TableRow key={order.order_number} hover sx={{ cursor: 'pointer' }}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {order.order_number}
                        </TableCell>
                        <TableCell>{order.customer_name || '—'}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{fmt(order.total_amount)}</TableCell>
                        <TableCell><StatusChip status={order.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Products + Low Stock */}
        <Grid item xs={12} lg={5}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Top Selling Vehicles</Typography>
              {(data?.top_products || []).map((p, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.8, borderBottom: i < 4 ? '1px solid #f0f0f0' : 'none' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.brand} · {p.units_sold} units</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700} color="primary">{fmt(p.revenue)}</Typography>
                </Box>
              ))}
              {!data?.top_products?.length && <Typography variant="body2" color="text.secondary">No sales data yet</Typography>}
            </CardContent>
          </Card>

          {/* Low Stock Alerts */}
          {(data?.low_stock_alerts || []).length > 0 && (
            <Card sx={{ border: '1px solid #fff3e0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Warning sx={{ color: 'warning.main' }} />
                  <Typography variant="h6" fontWeight={700}>Low Stock Alerts</Typography>
                </Box>
                {data.low_stock_alerts.map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2">{item.name}</Typography>
                    <Chip label={`${item.quantity} left`} size="small" color="warning" />
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
